import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiBarChart2, FiCalendar, FiUser } from 'react-icons/fi';
import api from '../../utils/api';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import { isAbandonedMatchFeedback } from '../../utils/matchFeedbackAbandoned';
import { isDaysMatchType } from '../../utils/matchTypeUi';
import {
  getRoleIconComponent,
  TEAM_MEMBER_MODAL_ACCENT,
} from '../../utils/teamMemberDetailModalUi';

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const dateText = formatDate(parsed);
  const timeText = parsed.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${dateText} ${timeText}`;
};

const getFeedbackDayLabel = (feedback, match) => {
  const rawDay =
    feedback?.feedbackDay ??
    feedback?.dayNumber ??
    feedback?.matchDay ??
    match?.feedbackDay ??
    match?.dayNumber ??
    match?.matchDay ??
    null;

  const numericDay = Number(rawDay);
  if (Number.isFinite(numericDay) && numericDay >= 1) {
    return `Day ${numericDay}`;
  }

  if (rawDay != null && String(rawDay).trim()) {
    return String(rawDay).trim();
  }

  return '';
};

const getFeedbackMatchKey = (feedback) => {
  const match = feedback?.matchId || {};
  const idKey = match?._id != null ? String(match._id) : '';
  if (idKey) return `id:${idKey}`;
  const nameKey = match?.matchName != null ? String(match.matchName).trim() : '';
  if (nameKey) return `name:${nameKey}`;
  return `fb:${String(feedback?._id || '')}`;
};

const getMatchDisplayLabel = (feedback, showDay = false) => {
  const match = feedback?.matchId || {};
  const baseName = match?.matchName || String(feedback?._id || match?._id || '-');
  const dayLabel = getFeedbackDayLabel(feedback, match);
  return showDay && dayLabel ? `${baseName} (${dayLabel})` : baseName;
};

const formatScoreText = (value) => {
  const text = String(value ?? '').trim().toUpperCase();
  if (text === 'N/A') return 'NA';
  return value;
};

const METRIC_BADGE_MUTED = { bg: '#f3f4f6', color: '#6b7280' };
const METRIC_VALUE_PLAIN = { bg: 'transparent', color: '#334155' };
const AVERAGE_BADGE_PURPLE = { bg: 'rgba(102, 126, 234, 0.16)', color: '#5b21b6' };

const formatMetricCellDisplay = (val) => {
  const text = formatScoreText(val ?? '-');
  if (text === 'NA' || text === '-') return text;
  const n = Number(val);
  if (Number.isFinite(n)) return n.toFixed(1);
  return String(val ?? '-');
};

const metricScoreBadge = (val) => {
  const text = formatScoreText(val ?? '-');
  if (text === 'NA' || text === '-') return METRIC_BADGE_MUTED;
  const n = Number(val);
  if (!Number.isFinite(n)) return METRIC_BADGE_MUTED;
  return METRIC_VALUE_PLAIN;
};

const averageScoreBadge = (val) => {
  const text = String(val ?? '').trim();
  if (text === 'NA' || text === '-') return METRIC_BADGE_MUTED;
  const n = Number(val);
  if (!Number.isFinite(n)) return METRIC_BADGE_MUTED;
  return AVERAGE_BADGE_PURPLE;
};

const getTeamDisplayName = (team) => {
  if (!team || typeof team !== 'object') return '';
  const name = team.teamName || team.name;
  if (name && String(name).trim()) return String(name).trim();
  const parts = [team.firstName, team.lastName].filter((p) => p && String(p).trim());
  if (parts.length) return parts.join(' ');
  return '';
};

const FEEDBACK_SUMMARY_CARD_OUTER = {
  marginBottom: '22px',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  background: '#f8fafc',
};

const SUMMARY_DETAILS_GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gap: '12px 18px',
  padding: '16px 18px',
};

const SUMMARY_DETAIL_ITEM = {
  minWidth: 0,
};

const SUMMARY_DETAIL_LABEL = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#98a2b3',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  margin: 0,
  lineHeight: 1.3,
};

const SUMMARY_DETAIL_VALUE = {
  margin: '4px 0 0',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0f172a',
  lineHeight: 1.4,
  wordBreak: 'break-word',
};

const SUMMARY_DETAIL_VALUE_SMALL = {
  ...SUMMARY_DETAIL_VALUE,
  fontSize: '14px',
  fontWeight: 600,
};

/** Synthetic id for “one table across all matches”. */
const VIEW_ALL_MATCHES = '__ALL_MATCHES__';

const round1 = (n) => (Number.isFinite(n) ? Math.round(n * 10) / 10 : null);

const extractTeamDataFromFeedbackList = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;

  const firstRecordWithTeam = list.find((item) => item?.eventTeamId);
  const teamCandidate = firstRecordWithTeam?.eventTeamId;

  if (teamCandidate && typeof teamCandidate === 'object') {
    return teamCandidate;
  }

  return null;
};

/** Merge all match feedback rows by member + role; average scores and totals per match appearance. */
function aggregateFeedbackAcrossMatches(allFeedbacks) {
  const orderedParams = [];
  const seenParam = new Set();
  for (const fb of allFeedbacks) {
    for (const entry of Array.isArray(fb.feedback) ? fb.feedback : []) {
      for (const s of Array.isArray(entry.scores) ? entry.scores : []) {
        const name = s?.parameterName;
        if (name && !seenParam.has(name)) {
          seenParam.add(name);
          orderedParams.push(name);
        }
      }
    }
  }

  const groupMap = new Map();
  for (const fb of allFeedbacks) {
    for (const entry of Array.isArray(fb.feedback) ? fb.feedback : []) {
      const emp = entry.employeeId;
      const empId =
        emp?._id != null ? String(emp._id)
          : emp != null && typeof emp !== 'object' ? String(emp)
            : '';
      const role = (entry.role || '').trim();
      const key = `${empId}|||${role}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          role,
          employeeId: emp,
          paramAgg: {},
          totalSum: 0,
          totalCount: 0,
          remarks: [],
          order: groupMap.size,
        });
      }
      const g = groupMap.get(key);

      for (const s of Array.isArray(entry.scores) ? entry.scores : []) {
        const pn = s?.parameterName;
        if (!pn) continue;
        const num = Number(s.score);
        if (!Number.isFinite(num)) continue;
        if (!g.paramAgg[pn]) g.paramAgg[pn] = { sum: 0, count: 0 };
        g.paramAgg[pn].sum += num;
        g.paramAgg[pn].count += 1;
      }
      const t = Number(entry.total);
      if (Number.isFinite(t)) {
        g.totalSum += t;
        g.totalCount += 1;
      }
      const r = entry.remarks && String(entry.remarks).trim();
      if (r) g.remarks.push(r);
    }
  }

  const entries = Array.from(groupMap.values()).map((g) => {
    const withScores = orderedParams.map((parameterName) => {
      const agg = g.paramAgg[parameterName];
      const avg = agg && agg.count ? agg.sum / agg.count : null;
      return { parameterName, score: avg != null ? round1(avg) : null };
    });

    const total = g.totalCount ? round1(g.totalSum / g.totalCount) : null;
    const uniqRemarks = [...new Set(g.remarks)];
    return {
      role: g.role,
      employeeId: g.employeeId,
      scores: withScores,
      total,
      remarks: uniqRemarks.length ? uniqRemarks.join('; ') : '-',
      order: g.order,
    };
  });

  // Keep first-seen order so S.No + Job Type flow stays close to single-match view.
  entries.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return { entries, allParams: orderedParams };
}

const TeamFeedbackViewPage = () => {
  const { eventTeamId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const appBasePath = location.pathname.startsWith('/employee') ? '/employee' : '/admin';

  const [loading, setLoading] = useState(false);
  const [allFeedbacks, setAllFeedbacks] = useState([]);   // all match feedbacks for this team
  const [teamData, setTeamData] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState('');

  useEffect(() => {
    if (!eventTeamId) return;
    const load = async () => {
      setLoading(true);
      try {
        const feedbackRes = await api.getMatchFeedbackByEventTeamId(eventTeamId);

        const raw = feedbackRes?.success ? feedbackRes.data : (feedbackRes?.data || feedbackRes);
        const list = Array.isArray(raw) ? raw
          : Array.isArray(raw?.feedbacks) ? raw.feedbacks
          : Array.isArray(raw?.data) ? raw.data
          : [];

        setAllFeedbacks(list);
        if (list.length > 0) {
          const requestedSelectedId = String(location.state?.selectedFeedbackId || '').trim();
          const hasRequestedSelection = requestedSelectedId
            && list.some((item) => String(item?._id || item?.matchId?._id || '') === requestedSelectedId);
          if (hasRequestedSelection) {
            setSelectedMatchId(requestedSelectedId);
          } else {
            const firstId = String(list[0]._id || list[0].matchId?._id || '');
            setSelectedMatchId(list.length > 1 ? VIEW_ALL_MATCHES : firstId);
          }
        }

        setTeamData(extractTeamDataFromFeedbackList(list));
      } catch (err) {
        if (err?.status !== 404) toast.error(err.message || 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventTeamId, location.state]);

  const handleBack = () => {
    const fromPath = String(location.state?.from || '').trim();
    navigate(fromPath || `${appBasePath}/teams-events`);
  };

  const isAllMatchesView =
    selectedMatchId === VIEW_ALL_MATCHES && allFeedbacks.length > 1;

  const nonAbandonedFeedbacks = useMemo(
    () => allFeedbacks.filter((fb) => !isAbandonedMatchFeedback(fb)),
    [allFeedbacks]
  );

  const multiDayMatchKeys = useMemo(() => {
    const keyToDays = new Map();
    allFeedbacks.forEach((fb) => {
      const match = fb?.matchId || {};
      if (!isDaysMatchType(match?.matchType)) return;
      const key = getFeedbackMatchKey(fb);
      const dayLabel = getFeedbackDayLabel(fb, match);
      if (!dayLabel) return;
      if (!keyToDays.has(key)) keyToDays.set(key, new Set());
      keyToDays.get(key).add(dayLabel);
    });

    const result = new Set();
    keyToDays.forEach((days, key) => {
      if (days.size > 1) result.add(key);
    });
    return result;
  }, [allFeedbacks]);

  const selectedFb = useMemo(() => {
    if (isAllMatchesView) return null;
    return (
      allFeedbacks.find(
        (fb) => String(fb._id || fb.matchId?._id || '') === selectedMatchId
      ) || null
    );
  }, [allFeedbacks, selectedMatchId, isAllMatchesView]);

  const { entries, allParams } = useMemo(() => {
    if (isAllMatchesView) {
      return aggregateFeedbackAcrossMatches(nonAbandonedFeedbacks);
    }
    const raw = Array.isArray(selectedFb?.feedback) ? selectedFb.feedback : [];
    const params = Array.from(
      new Set(
        raw.flatMap((entry) =>
          Array.isArray(entry.scores)
            ? entry.scores.filter(Boolean).map((s) => s.parameterName).filter(Boolean)
            : []
        )
      )
    );
    return { entries: raw, allParams: params };
  }, [selectedFb, isAllMatchesView, nonAbandonedFeedbacks]);

  const getScore = (entry, paramName) => {
    if (!Array.isArray(entry.scores)) return null;
    const found = entry.scores.find((s) => s.parameterName === paramName);
    return found ? found.score : null;
  };

  const match = selectedFb?.matchId || {};
  const evaluatorName = selectedFb?.evaluator?.name || selectedFb?.evaluator?.fullName ||
    (typeof selectedFb?.evaluator === 'string' ? selectedFb.evaluator : '');
  const selectedMatchIsAbandoned = selectedFb ? isAbandonedMatchFeedback(selectedFb) : false;
  const teamDisplayName = getTeamDisplayName(teamData);
  /** Average of per-match `averageTotal` (summary strip). */
  const teamOverallRating = useMemo(() => {
    const nums = nonAbandonedFeedbacks
      .map((fb) => fb.averageTotal)
      .filter((v) => v != null && Number.isFinite(Number(v)))
      .map(Number);
    if (!nums.length) return null;
    const sum = nums.reduce((a, b) => a + b, 0);
    return Math.round((sum / nums.length) * 10) / 10;
  }, [nonAbandonedFeedbacks]);

  const overallViewOptionLabel = useMemo(() => {
    return 'All Matches';
  }, []);

  const matchSelectOptions = useMemo(() => {
    const opts = [];
    if (allFeedbacks.length > 1) {
      opts.push({ value: VIEW_ALL_MATCHES, label: overallViewOptionLabel });
    }
    allFeedbacks.forEach((fb) => {
      const m = fb.matchId || {};
      const id = String(fb._id || m._id || '');
      const label = getMatchDisplayLabel(fb, multiDayMatchKeys.has(getFeedbackMatchKey(fb)));
      opts.push({ value: id, label });
    });
    return opts;
  }, [allFeedbacks, overallViewOptionLabel, multiDayMatchKeys]);

  const selectedMatchOverallFeedbackText = useMemo(
    () => (selectedFb ? String(selectedFb.overallFeedback ?? '').trim() : ''),
    [selectedFb]
  );

  const showMatchSelector = !loading && allFeedbacks.length > 0;

  const pageHeader = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        marginBottom: '22px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', minWidth: 0 }}>
        <div
          aria-hidden
          style={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'rgba(102, 126, 234, 0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: '#667eea',
          }}
        >
          <FiBarChart2 size={24} strokeWidth={2} />
        </div>
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 700,
              color: '#1e1b4b',
              letterSpacing: '-0.02em',
            }}
          >
            Match Performance
          </h1>
          <p
            style={{
              margin: '6px 0 0',
              fontSize: '13px',
              color: '#64748b',
              lineHeight: 1.45,
              maxWidth: '540px',
            }}
          >
            Member scores and averages for this team.
          </p>
        </div>
      </div>
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'nowrap',
          justifyContent: 'flex-end',
        }}
      >
        {showMatchSelector && (
          <div style={{ flex: '0 1 280px', minWidth: '200px', maxWidth: '280px' }}>
            <StyledSelectDropdown
              omitEmptyOption
              placeholder={overallViewOptionLabel}
              triggerBorderColor="#e5e7eb"
              triggerHeight={36}
              value={selectedMatchId}
              onChange={(v) => setSelectedMatchId(v)}
              options={matchSelectOptions}
              leftIcon={<FiCalendar size={16} strokeWidth={2} style={{ color: '#667eea', flexShrink: 0 }} aria-hidden />}
              ariaLabel="Match or all matches"
            />
          </div>
        )}
        <button
          type="button"
          onClick={handleBack}
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: '36px',
            padding: '0 12px',
            border: '1px solid #e5e7eb',
            background: '#ffffff',
            color: '#1f2937',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
            boxSizing: 'border-box',
          }}
        >
          <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
          Back
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '20px', background: 'transparent', minHeight: '100%' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(15, 23, 42, 0.07)',
          border: '1px solid #e5e7eb',
          padding: '22px 24px 26px',
        }}
      >
        {pageHeader}

        {loading ? (
          <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '40px 0' }}>Loading...</div>
        ) : allFeedbacks.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '40px 0' }}>
            No match feedback found for this team.
          </div>
        ) : (
          <>
            {(selectedFb || isAllMatchesView) &&
              (isAllMatchesView ? (
                <div style={FEEDBACK_SUMMARY_CARD_OUTER} role="region" aria-label="Feedback summary">
                  <div style={SUMMARY_DETAILS_GRID}>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>View</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>All Matches</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Team</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{teamDisplayName || '—'}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Total Matches</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{allFeedbacks.length}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Average Rating</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>
                        {teamOverallRating != null ? `${teamOverallRating.toFixed(1)} / (5.0)` : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={FEEDBACK_SUMMARY_CARD_OUTER} role="region" aria-label="Match feedback summary">
                  <div style={SUMMARY_DETAILS_GRID}>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Match Name</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{match.matchName || '—'}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Venue</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{match.matchLocation || '—'}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Team</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{teamDisplayName || '—'}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Start Date & Time</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{formatDateTime(match.startDate)}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>End Date & Time</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>{formatDateTime(match.endDate)}</p>
                    </div>
                    <div style={SUMMARY_DETAIL_ITEM}>
                      <p style={SUMMARY_DETAIL_LABEL}>Average Rating</p>
                      <p style={SUMMARY_DETAIL_VALUE_SMALL}>
                        {selectedMatchIsAbandoned
                          ? 'NA'
                          : selectedFb?.averageTotal != null
                            ? `${Number(selectedFb.averageTotal).toFixed(1)} / (5.0)`
                            : '—'}
                      </p>
                    </div>
                    {evaluatorName ? (
                      <div style={SUMMARY_DETAIL_ITEM}>
                        <p style={SUMMARY_DETAIL_LABEL}>Evaluator</p>
                        <p style={SUMMARY_DETAIL_VALUE_SMALL}>{evaluatorName}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}

            {/* {isAllMatchesView && (
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#667085' }}>
                Each cell is the average of that score across every match where this member has this role.
              </p>
            )} */}

            {/* Feedback table */}
            {entries.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '24px 0' }}>
                {isAllMatchesView ? 'No feedback entries across these matches.' : 'No feedback entries for this match.'}
              </div>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${300 + allParams.length * 110}px` }}>
                    <thead
                      style={{
                        background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                      }}
                    >
                      <tr>
                        <th
                          style={{
                            padding: '12px 14px',
                            textAlign: 'center',
                            fontSize: '12px',
                            color: '#f8fafc',
                            fontWeight: 700,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                            width: '52px',
                          }}
                        >
                          S.No
                        </th>
                        <th
                          style={{
                            padding: '12px 14px',
                            textAlign: 'left',
                            fontSize: '12px',
                            color: '#f8fafc',
                            fontWeight: 700,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          }}
                        >
                          Job Type
                        </th>
                        <th
                          style={{
                            padding: '12px 14px',
                            textAlign: 'left',
                            fontSize: '12px',
                            color: '#f8fafc',
                            fontWeight: 700,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          }}
                        >
                          Member
                        </th>
                        {allParams.map((p) => (
                          <th
                            key={p}
                            style={{
                              padding: '12px 14px',
                              textAlign: 'center',
                              fontSize: '12px',
                              color: '#f8fafc',
                              fontWeight: 700,
                              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {p}
                          </th>
                        ))}
                        <th
                          style={{
                            padding: '12px 14px',
                            textAlign: 'center',
                            fontSize: '12px',
                            color: '#f8fafc',
                            fontWeight: 700,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          }}
                        >
                          Average
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, idx) => {
                        const totalNumber = Number(entry.total);
                        const total = Number.isFinite(totalNumber)
                          ? totalNumber.toFixed(1)
                          : formatScoreText(entry.total ?? '-');
                        const totalBadge = averageScoreBadge(
                          Number.isFinite(totalNumber) ? totalNumber : entry.total
                        );
                        const empKey = entry.employeeId?._id ?? entry.employeeId?.id ?? idx;
                        const RoleIcon = getRoleIconComponent(entry.role);
                        return (
                          <tr
                            key={`${entry.role || ''}-${empKey}-${idx}`}
                            style={{ borderTop: '1px solid #e5e7eb', background: '#ffffff' }}
                          >
                            <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                              {idx + 1}
                            </td>
                            <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                                <RoleIcon size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                                {entry.role || '-'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                                <FiUser size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                                {entry.employeeId?.name || entry.employeeId?.fullName || '-'}
                              </span>
                            </td>
                            {allParams.map((p) => {
                              const val = getScore(entry, p);
                              const { bg, color } = metricScoreBadge(val);
                              return (
                                <td key={p} style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      minWidth: '32px',
                                      padding: '4px 10px',
                                      borderRadius: '8px',
                                      background: bg,
                                      color,
                                      fontSize: '12px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {formatMetricCellDisplay(val)}
                                  </span>
                                </td>
                              );
                            })}
                            <td style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  minWidth: '36px',
                                  padding: '4px 10px',
                                  borderRadius: '8px',
                                  background: totalBadge.bg,
                                  color: totalBadge.color,
                                  fontSize: '12px',
                                  fontWeight: 700,
                                }}
                              >
                                {total}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Overall feedback only when a single match is selected (hidden for Overall / all-matches dropdown) */}
            {!isAllMatchesView && selectedFb && (
              <div
                style={{
                  marginTop: '14px',
                  padding: '10px 12px',
                  width: '50%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  background: '#f9fafb',
                  borderRadius: '10px',
                  border: '1px solid #eaecf0',
                }}
              >
                <div style={{ fontSize: '12px', color: '#667085', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.2px' }}>Overall Feedback</div>
                <div
                  style={{
                    fontSize: '13px',
                    lineHeight: 1.6,
                    color: selectedMatchOverallFeedbackText ? '#344054' : '#98a2b3',
                  }}
                >
                  {selectedMatchOverallFeedbackText || 'No feedback'}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamFeedbackViewPage;
